import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMachine } from '@xstate/react';
import { useHistory } from 'react-router';
import { useSelectedProvider } from '../components/App/ProviderContainer/useSelectedProvider';
import { SuccessNotification } from '../core/screens/SuccessNotification/SuccessNotification';
import { LoadingOverlay } from '../core/screens/LoadingOverlay/LoadingOverlay';
import { applicationParameters } from '../utilities/application-parameters';
import { ReduxCredentials } from '../components/screens/SubmitCredentials/ReduxCredentials';
import { AccountList } from './components/BundledFlow/molecules/AccountList';
import { messages as bundledFlowMessages } from './components/BundledFlow/molecules/messages';
import { getMachine } from './reportsMachine';

import {
  ApplicationAsyncReportsResult,
  ApplicationReportsResult,
} from './components/ReportsDirector/ReportsResult';
import { ReportDescriptionResponse } from './models/report';
import { useIdempotencyKey } from './useIdempotencyKey';

export type ReportsMachineViewProps = {
  onCancel: () => void;
  reportDescription: ReportDescriptionResponse;
  externalReference?: string;
};

// istanbul ignore next: this component is a switch.
export const ReportsMachineView: React.FC<ReportsMachineViewProps> = ({
  onCancel,
  reportDescription,
  externalReference,
}) => {
  const { selectedProvider } = useSelectedProvider();
  const history = useHistory();

  const asyncFlow = applicationParameters.get('async') || false;
  const reportTypes = applicationParameters.get('reportTypes')!;
  const customerReferenceId = applicationParameters.get('customerReferenceId');
  const { refreshItems, accountDialogType } = reportDescription;

  const idempotencyKey = useIdempotencyKey();

  const machine = useMemo(
    () =>
      getMachine({
        accountDialogType,
        asyncFlow,
        getAccountsPayload: {
          reportTypes,
          accountDialogType,
          refreshableItems: refreshItems,
        },
        generateReportsPayload: {
          accountDialogType,
          customerReferenceId,
          externalReference,
          refreshableItems: refreshItems,
          reportTypes,
        },
        generateReportsAsyncPayload: {
          customerReferenceId,
          externalReference,
          refreshableItems: refreshItems,
          reportTypes,
        },
      }),
    [
      accountDialogType,
      customerReferenceId,
      refreshItems,
      reportTypes,
      asyncFlow,
      externalReference,
    ]
  );
  const [state, send] = useMachine(machine);

  const { accountsResponse, generateReportsResponse, generateReportsAsyncResponse, error } =
    state.context;

  if (state.matches('auth')) {
    return (
      <ReduxCredentials
        onBack={() => {
          onCancel();
          // ReduxCredentials doesn't reliably call the onBack callback,
          // instead sometimes doing history.goBack by itself, so we're doing
          // the same to align until we fix the issue.
          // TODO: use send() to manage the navigation in XState
          history.goBack();
        }}
        onDone={(credentials) => {
          send('SET_IDEMPOTENCY_KEY', { data: idempotencyKey });

          return send('CREDENTIALS', { data: [credentials.id] });
        }}
        provider={selectedProvider}
        refreshableItems={refreshItems}
        waitForUpdated={!asyncFlow}
      />
    );
  }

  if (state.matches('SUCCESS')) {
    const applicationSuccessResult = asyncFlow
      ? new ApplicationAsyncReportsResult(generateReportsAsyncResponse!)
      : new ApplicationReportsResult(generateReportsResponse!);

    return (
      <SuccessNotification
        applicationSuccessResult={applicationSuccessResult}
        forceSkipSuccessScreen
      />
    );
  }

  if (state.matches('ERROR')) {
    throw error;
  }

  if (state.matches('SINGLE_ACCOUNT_SELECTION_VIEW')) {
    return (
      <AccountList
        title={<FormattedMessage {...bundledFlowMessages.selectAccount} />}
        allowSelectMultipleAccounts={false}
        accounts={accountsResponse!}
        providerDisplayName={selectedProvider.displayName}
        onCancel={onCancel}
        onSubmit={(accountIds) => {
          send('EVENT_ACCOUNTS_SELECTED', {
            data: accountIds,
          });
        }}
      />
    );
  }

  if (state.matches('MULTIPLE_ACCOUNT_SELECTION_VIEW')) {
    return (
      <AccountList
        title={<FormattedMessage {...bundledFlowMessages.selectAccounts} />}
        allowSelectMultipleAccounts
        accounts={accountsResponse!}
        providerDisplayName={selectedProvider.displayName}
        onCancel={onCancel}
        onSubmit={(accountIds) => {
          send('EVENT_ACCOUNTS_SELECTED', {
            data: accountIds,
          });
        }}
      />
    );
  }

  return <LoadingOverlay />;
};
